import { useCallback, useRef } from "react";

import { clampRestoredPageToLoaded } from "../utils/clamp-restored-page-on-walk-give-up";

/**
 * Pairs the two "we could not put you back exactly" fallbacks so they stop
 * fighting each other.
 *
 * Real-world case: you left All Activity on page 5 and hit Back. The network
 * fails partway through loading pages 2…5, so we can only show page 2. That is
 * "we could not reach your page", not "the alert you clicked is gone" — the
 * page-1 reset that handles a deleted row must not fire, or it throws away the
 * pages we did manage to load and drops you at the top of the list.
 *
 * When the walk did *not* clamp (the target page loaded fine and the row still
 * is not there), the row really is gone and page 1 is the right answer.
 */
export const useRestoreGiveUpClamp = (options: {
	currentPage: number;
	loadedPages: number;
	setCurrentPage: (page: number) => void;
}): {
	onAnchorMissing: () => void;
	onGiveUp: () => void;
} => {
	const { currentPage, loadedPages, setCurrentPage } = options;

	// Ref, not state: `onAnchorMissing` is a dependency of the restore-scroll
	// effect, so a changing identity would re-run the restore mid-visit.
	const clampedRef = useRef(false);

	const onGiveUp = useCallback(() => {
		// Walk failed — show the deepest loaded page, not page 1.
		if (clampRestoredPageToLoaded(currentPage, setCurrentPage, loadedPages)) {
			clampedRef.current = true;
		}
	}, [currentPage, loadedPages, setCurrentPage]);

	const onAnchorMissing = useCallback(() => {
		// AC8: the saved row is no longer in the dataset — start at page 1.
		if (clampedRef.current) {
			return;
		}
		setCurrentPage(1);
	}, [setCurrentPage]);

	return { onAnchorMissing, onGiveUp };
};
